import { Button, LoadingSpinner, Modal, SearchBar } from "@ai-teach/ui-web";
import { useState } from "react";
import { FaRegNewspaper } from "react-icons/fa";
import { HiFolder } from "react-icons/hi";
import { MdChevronRight, MdDriveFileMove } from "react-icons/md";

import { useSelection } from "@/contexts/SelectionContext";
import { useLessonNavigation } from "@/lib/hooks/modal/useLessonNavigation";
import { useDuplicateSlide } from "@/lib/hooks/slides/useDuplicateSlide";
import { useDuplicateSlides } from "@/lib/hooks/slides/useDuplicateSlides";

interface DuplicateSlideModalProps {
  isOpen: boolean;
  defaultLessonId?: string | null;
  defaultProjectStack?: Array<{ id: string; name: string }> | undefined;
}

export function DuplicateSlideModal({
  isOpen,
  defaultLessonId,
  defaultProjectStack,
}: DuplicateSlideModalProps) {
  const [searchText, setSearchText] = useState("");
  const [nameError, setNameError] = useState("");
  const [duplicateType, setDuplicateType] = useState<"unique" | "linked">("unique");

  const {
    selectedSlideIds,
    isDuplicateSlideModalOpen,
    clearAllSelections,
    toggleDuplicateSlideModal,
  } = useSelection();

  const [slideName, setSlideName] = useState(isDuplicateSlideModalOpen || "");

  const {
    currentLessonId,
    projects,
    lessonsWithoutLimitation,
    isEmpty,
    currentParent,
    navigationStack,
    hasMoreLesson,
    hasMoreProjects,
    lessonLoading,
    projectLoading,
    handleProjectClick,
    handleLessonClick,
    navigateToIndex,
    resetNavigation,
    loadMoreProjects,
    loadMoreLesson,
  } = useLessonNavigation(searchText, {
    initialLessonId: defaultLessonId,
    initialProjectStack: defaultProjectStack,
  });

  const lessonsProjectsLoading = lessonLoading || projectLoading;

  const isMultiSlide = selectedSlideIds.length > 1;

  const { duplicateSlide, loading: duplicateSlideLoading } = useDuplicateSlide();
  const { duplicateSlides, loading: duplicateSlidesLoading } = useDuplicateSlides();

  const duplicateLoading = duplicateSlideLoading || duplicateSlidesLoading;

  const validateName = (name: string): string => {
    if (!name.trim()) {
      return "Name is required";
    }
    if (name.length > 100) {
      return "Name must be 100 characters or less";
    }
    return "";
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSlideName(value);
    setNameError(validateName(value));
  };

  const isNameValid = isMultiSlide || (slideName.trim() && slideName.length <= 100);

  const handleDuplicate = async () => {
    if (!isNameValid || !currentLessonId) return;
    try {
      if (isMultiSlide) {
        await duplicateSlides(selectedSlideIds, currentLessonId, duplicateType);
      } else if (selectedSlideIds[0]) {
        await duplicateSlide(
          selectedSlideIds[0],
          currentLessonId,
          slideName.trim(),
          duplicateType,
          duplicateType === "linked" ? selectedSlideIds[0] : null
        );
      }
      handleClose();
    } catch (error) {
      // Error already handled by hook's toast
      console.error("Duplication failed:", error);
    }
  };

  const handleClose = () => {
    resetNavigation();
    setSearchText("");
    setSlideName("");
    setNameError("");
    toggleDuplicateSlideModal(false);
    clearAllSelections();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isMultiSlide ? `Duplicate ${selectedSlideIds.length} Slides` : "Duplicate Slide"}
    >
      <div className="space-y-4">
        <p className="text-sm text-secondary-200">
          Do you want to create a <strong>Unique</strong> or a <strong>Linked</strong> copy?
        </p>

        <div className="flex w-full gap-3 justify-between">
          {/* Name input — single slide only */}
          {!isMultiSlide && (
            <div className="space-y-1 flex-1">
              <label className="label flex items-center gap-1">
                <span className="text-red-500">*</span> Duplicate Slide Name
              </label>
              <input
                type="text"
                value={slideName}
                onChange={handleNameChange}
                placeholder="Enter slide name"
                className="w-full bg-surface border border-gray-800 rounded-md px-3 py-2 text-sm text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {nameError && <p className="text-xs text-red-500">{nameError}</p>}
            </div>
          )}

          {/* Duplicate Type Dropdown */}
          <div className="space-y-1">
            <label className="label flex items-center gap-1">
              <span className="text-red-500">*</span> Duplication Type
            </label>

            <select
              value={duplicateType}
              onChange={(e) => setDuplicateType(e.target.value as "unique" | "linked")}
              className="w-full bg-surface border border-gray-800 rounded-md px-3 py-2 text-sm text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="unique">Unique</option>
              <option value="linked">Linked</option>
            </select>
          </div>
        </div>

        <p className="label flex items-center gap-1">
          <span className="text-red-500">*</span> Duplicate destination
        </p>

        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search projects, lessons..."
        />

        <nav
          aria-label="Folder breadcrumb"
          className="flex items-center gap-1 text-sm min-w-0 overflow-hidden flex-wrap"
        >
          <button
            type="button"
            onClick={() => navigateToIndex(-1)}
            className={`flex-shrink-0 font-medium transition-colors ${
              navigationStack.length === 0
                ? "text-secondary-200 cursor-default"
                : "text-gray-400 hover:text-primary-500"
            }`}
          >
            Home
          </button>
          {navigationStack.map((item, i) => {
            const isLast = i === navigationStack.length - 1;
            return (
              <span key={item.id} className="flex items-center gap-1 min-w-0">
                <MdChevronRight className="w-4 h-4 flex-shrink-0 text-gray-600" />
                <button
                  type="button"
                  onClick={() => navigateToIndex(i)}
                  className={`truncate font-medium transition-colors ${
                    isLast
                      ? "text-secondary-200 cursor-default"
                      : "text-gray-400 hover:text-primary-500"
                  }`}
                >
                  {item.name}
                </button>
              </span>
            );
          })}
        </nav>

        <div className="h-96 overflow-y-auto border border-gray-800 rounded-lg gradient-scrollbar">
          {lessonsProjectsLoading &&
          projects.length === 0 &&
          lessonsWithoutLimitation.length === 0 ? (
            <div className="p-4 flex gap-2 flex-col items-center justify-center h-full caption">
              <LoadingSpinner />
              <p className="animate-pulse">Loading...</p>
            </div>
          ) : isEmpty ? (
            <div className="p-4 flex flex-col items-center justify-center gap-2 h-full caption">
              <MdDriveFileMove fontSize={40} color="#1ca0ff" />
              {currentParent
                ? "No projects or lessons found in this folder"
                : "No projects or lessons found"}
            </div>
          ) : (
            <>
              {/* Projects Section */}
              {projects.length > 0 && (
                <>
                  <div className="px-3 py-2 text-xs font-semibold text-white border-b border-gray-800 bg-primary-400 text-center ">
                    Projects
                  </div>
                  {projects.map((project) => {
                    const isDisabled = project.isLinkedCopy || project.hasLinkedCopy;
                    return (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() =>
                          !isDisabled && handleProjectClick({ id: project.id, name: project.name })
                        }
                        disabled={isDisabled}
                        className={`w-full flex items-center justify-between p-3 border-b border-gray-800 last:border-b-0 transition-colors
                          ${
                            isDisabled
                              ? "bg-gray-800/20 cursor-not-allowed opacity-60"
                              : "hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                          }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <HiFolder
                            className={`w-5 h-5 flex-shrink-0 ${isDisabled ? "text-gray-500" : "text-primary-500"}`}
                          />
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className={`font-medium truncate ${isDisabled ? "text-gray-500" : "text-secondary-200"}`}
                            >
                              {project.name}
                            </div>
                            {project.isLinkedCopy && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
                                Linked Copy
                              </span>
                            )}
                            {project.hasLinkedCopy && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-red-400 rounded-full">
                                Has linked copy
                              </span>
                            )}
                          </div>
                        </div>
                        {!isDisabled && (
                          <MdChevronRight className="w-5 h-5 text-secondary-200 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                  {hasMoreProjects && (
                    <button
                      type="button"
                      onClick={loadMoreProjects}
                      disabled={projectLoading}
                      className="w-full p-3 flex items-center justify-center gap-2 text-sm text-primary-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {projectLoading ? (
                        <>
                          <div className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Load more projects..."
                      )}
                    </button>
                  )}
                </>
              )}

              {/* Lessons Section */}
              {lessonsWithoutLimitation.length > 0 && (
                <>
                  <div className="px-3 py-2 text-xs font-semibold text-white border-b border-gray-800 bg-primary-400 text-center ">
                    Lessons
                  </div>
                  {lessonsWithoutLimitation.map((lesson) => {
                    const isDisabled =
                      lesson.isLinkedCopy || (lesson.hasLinkedCopy && duplicateType === "linked");
                    const isSelected = currentLessonId === lesson.id;
                    return (
                      <button
                        key={lesson.id}
                        type="button"
                        onClick={() =>
                          !isDisabled && handleLessonClick({ id: lesson.id, name: lesson.name })
                        }
                        disabled={isDisabled}
                        className={`w-full flex items-center justify-between p-3 border-b border-gray-800 last:border-b-0 transition-colors
                          ${isSelected ? "bg-primary-500/10 ring-2 ring-primary-500" : ""}
                          ${
                            isDisabled
                              ? "bg-gray-800/20 cursor-not-allowed opacity-60"
                              : "hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                          }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FaRegNewspaper
                            className={`w-5 h-5 flex-shrink-0 ${isDisabled ? "text-gray-500" : "text-green-500"}`}
                          />
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className={`font-medium truncate ${isDisabled ? "text-gray-500" : "text-secondary-200"}`}
                            >
                              {lesson.name}
                            </div>
                            {lesson.isLinkedCopy && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
                                Linked Copy
                              </span>
                            )}
                            {lesson.hasLinkedCopy && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-red-400 rounded-full">
                                Has linked copy
                              </span>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <span className="text-xs font-semibold text-primary-500 flex-shrink-0">
                            Selected
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {hasMoreLesson && (
                    <button
                      type="button"
                      onClick={loadMoreLesson}
                      disabled={lessonLoading}
                      className="w-full p-3 flex items-center justify-center gap-2 text-sm text-primary-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {lessonLoading ? (
                        <>
                          <div className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Load more lessons..."
                      )}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-gray-800">
          <Button variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleDuplicate}
            disabled={!isNameValid || !currentLessonId || duplicateLoading}
          >
            {duplicateLoading ? "Duplicating..." : "Duplicate"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
